import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import { fetchRoomMessages, sendRoomMessage } from '~/data/interact'
import { parseApiTime } from '~/data/meetings'
import type { Person, Pulse } from '~/data/types'
import type { EventBus } from '~/realtime/bus'
import { getHub } from '~/realtime/hub'
import { theme } from '~/theme'
import { AgentCharacter, showsCharacter } from './AgentAvatar'
import { PersonPeek } from './PersonPeek'

// The room panel: the live conversation and people of a pulse, with a
// composer. Tasks and notes live on the room's wall furniture (the corkboard
// and the shelf) — this panel is purely the conversation.

function timeLabel(createdAt: string): string {
  const ms = parseApiTime(createdAt)
  if (Number.isNaN(ms)) return ''
  const d = new Date(ms)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  if (sameDay) return hm
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${hm}`
}

function Avatar({ src, name, size = 22 }: { src: string | null; name: string; size?: number }) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={{ width: size, height: size, borderRadius: '50%', flex: '0 0 auto', objectFit: 'cover' }}
      />
    )
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flex: '0 0 auto',
        background: '#2a2e38',
        color: theme.cream,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.42,
        fontWeight: 600,
      }}
    >
      {name.slice(0, 1).toUpperCase()}
    </div>
  )
}

export function RoomPanel({
  pulse,
  organizationId,
  meId,
  people,
  bus,
  getToken,
  onClose,
  onFocusPerson,
  onLocate,
  isOwner,
  compact,
}: {
  pulse: Pulse
  organizationId: string
  meId: string | null
  people: Person[]
  bus: EventBus
  getToken: GetToken
  onClose: () => void
  onFocusPerson: (userId: string) => void
  /** Glide the camera to this room on the floor. */
  onLocate?: () => void
  /** Org owner — unlocks agent internals in the drill-down peek. */
  isOwner?: boolean
  compact?: boolean
}) {
  const queryClient = useQueryClient()
  const [topicId, setTopicId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [personView, setPersonView] = useState<Person | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const client = async () => createClient(await getToken())
  const myName = people.find((p) => p.userId === meId)?.name ?? 'Someone'
  // Typing has no server trigger — post it to the hub so others see "…is
  // typing" (topic-scoped when a topic is selected).
  const notifyTyping = (isTyping: boolean) => {
    if (!meId) return
    getHub(getToken).notifyTyping({
      pulseId: pulse.id,
      userId: meId,
      userName: myName,
      organizationId,
      isTyping,
    })
  }

  const conversation = useQuery({
    queryKey: ['roomMessages', pulse.id, topicId],
    queryFn: async () =>
      fetchRoomMessages(await client(), { pulseId: pulse.id, organizationId, topicId }),
    refetchOnWindowFocus: false,
  })

  // Live: any real message event for THIS pulse refreshes the panel.
  useEffect(() => {
    const off = bus.on((event) => {
      if (event.kind === 'message' && event.pulseId === pulse.id && !event.ambient) {
        void queryClient.invalidateQueries({ queryKey: ['roomMessages', pulse.id] })
      }
    })
    return off
  }, [bus, pulse.id, queryClient])

  // Keep the conversation pinned to the newest message. Keyed on the LAST
  // message id — the API serves a fixed-size page, so on a new message the
  // list LENGTH stays constant (oldest falls off) and length wouldn't retrigger.
  const lastMessageId = conversation.data?.messages.at(-1)?.id
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
    // personView: returning from the drill-down mounts a fresh messages div —
    // re-pin it to the newest message.
  }, [lastMessageId, topicId, personView])

  const send = useMutation({
    mutationFn: async () => {
      const text = draft.trim()
      if (!text) return
      // Missing identity must FAIL (preserving the draft via onError), not
      // resolve as a silent no-op that wipes the composer.
      if (!meId) throw new Error('Still signing you in — try again in a moment')
      const threadId = conversation.data?.teamThreadId ?? pulse.teamThreadId
      if (!threadId) throw new Error('This room has no message thread')
      const sent = await sendRoomMessage(await client(), {
        teamThreadId: threadId,
        userId: meId,
        text,
        topicId,
      })
      // Team messages are ALSO server-triggered, but the trigger is off in some
      // envs (e.g. staging) — fire the same belt-and-suspenders hub notify that
      // GameView does for person messages, carrying topic_id so a per-topic mute
      // is honored on this path too (without it the hub reads it as main-channel
      // and the muted topic still pushes).
      if (sent.id) {
        getHub(getToken).notifyTeamMessage({
          pulseId: pulse.id,
          pulseName: pulse.name,
          senderId: meId,
          senderName: myName,
          messageId: sent.id,
          messagePreview: text,
          organizationId,
          teamThreadId: threadId,
          topicId,
          topicName: topicId
            ? (pulse.topics.find((t) => t.id === topicId)?.name ?? null)
            : null,
        })
      }
    },
    onSuccess: () => {
      setDraft('')
      setError(null)
      notifyTyping(false)
      void queryClient.invalidateQueries({ queryKey: ['roomMessages', pulse.id] })
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Send failed'),
  })

  const personById = new Map(people.map((p) => [p.userId, p]))
  const members = pulse.memberUserIds
    .map((id) => personById.get(id))
    .filter((p): p is Person => Boolean(p))

  // In-panel drill-down (Football Manager style): clicking a member navigates
  // THIS panel to their peek with a back arrow, instead of spawning windows.
  if (personView) {
    return (
      <aside style={compact ? sheetCompact : sheet}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => setPersonView(null)}
            title={`Back to ${pulse.name}`}
            style={{ ...closeBtn, fontSize: 15 }}
          >
            ←
          </button>
          {/* A pictureless agent's character in place of the ✦. */}
          {showsCharacter(personView) && <AgentCharacter userId={personView.userId} size={26} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.cream, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {personView.isAgent && !showsCharacter(personView) ? '✦ ' : ''}
              {personView.name}
            </div>
            <div style={{ fontSize: 10.5, color: theme.textDim }}>
              {pulse.name} › {personView.jobTitle || personView.role || (personView.isAgent ? 'AI agent' : 'member')}
            </div>
          </div>
          <button
            onClick={() => {
              onFocusPerson(personView.userId)
              setPersonView(null)
            }}
            title="Open as their own window"
            style={{ ...closeBtn, fontSize: 12 }}
          >
            ↗
          </button>
          <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
        </div>
        <PersonPeek
          key={personView.userId}
          person={personView}
          organizationId={organizationId}
          getToken={getToken}
          isOwner={isOwner ?? false}
        />
      </aside>
    )
  }

  return (
    <aside style={compact ? sheetCompact : sheet}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.cream, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pulse.name}
          </div>
          <div style={{ fontSize: 11, color: theme.textDim }}>
            {pulse.category === 'ONETOONE' ? 'Direct message' : `${members.length} members`}
          </div>
        </div>
        {onLocate && (
          <button
            onClick={onLocate}
            title="Glide the camera to this room"
            style={{ ...closeBtn, fontSize: 13 }}
          >
            📍
          </button>
        )}
        <button onClick={onClose} style={closeBtn} aria-label="Close room">×</button>
      </div>

      {/* Topics */}
      {pulse.topics.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          <button
            onClick={() => setTopicId(null)}
            style={topicId === null ? chipActive : chip}
          >
            All
          </button>
          {pulse.topics.slice(0, 6).map((t) => {
            const tid = t.id
            if (!tid) return null
            return (
              <button
                key={tid}
                onClick={() => setTopicId(topicId === tid ? null : tid)}
                style={topicId === tid ? chipActive : chip}
              >
                #{t.name}
              </button>
            )
          })}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} style={messagesBox}>
        {conversation.isLoading && <div style={dimNote}>Loading the conversation…</div>}
        {conversation.isError && <div style={dimNote}>Couldn't load messages.</div>}
        {conversation.data?.messages.length === 0 && (
          <div style={dimNote}>No messages here yet — start the conversation below.</div>
        )}
        {conversation.data?.messages.map((m) => (
          <div key={m.id} style={{ display: 'flex', gap: 8, padding: '5px 0', alignItems: 'flex-start' }}>
            {m.userId && showsCharacter(personById.get(m.userId)) ? (
              <AgentCharacter userId={m.userId} size={22} />
            ) : (
              <Avatar src={m.avatar} name={m.name} />
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: m.userId === meId ? theme.terracottaLite : theme.cream }}>
                  {m.name}
                </span>
                <span style={{ fontSize: 10, color: theme.textDim }}>{timeLabel(m.createdAt)}</span>
                {m.topicName && !topicId && (
                  <span style={{ fontSize: 10, color: theme.terracottaLite }}>#{m.topicName}</span>
                )}
              </div>
              <div style={{ fontSize: 12.5, color: theme.text, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                {m.text || '…'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!send.isPending) send.mutate()
        }}
        style={{ display: 'flex', gap: 6, marginTop: 8 }}
      >
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value)
            notifyTyping(e.target.value.trim().length > 0)
          }}
          onBlur={() => notifyTyping(false)}
          placeholder={topicId ? 'Message this topic…' : 'Message this room…'}
          style={inputStyle}
          maxLength={4000}
        />
        <button type="submit" disabled={send.isPending || !draft.trim()} style={sendBtn}>
          {send.isPending ? '…' : 'Send'}
        </button>
      </form>
      {error && <div style={{ fontSize: 11, color: '#ff8a8a', marginTop: 4 }}>{error}</div>}

      {/* Members */}
      {members.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={sectionTitle}>People</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {members.slice(0, 18).map((p) => (
              <button
                key={p.userId}
                onClick={() => setPersonView(p)}
                title={`${p.name}${p.isAgent ? ' · AI agent' : ''}`}
                style={{ position: 'relative', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                {showsCharacter(p) ? (
                  // Its character with its role's emblem where the dot would sit.
                  <AgentCharacter userId={p.userId} size={26} />
                ) : (
                  <>
                    <Avatar src={p.picture || p.gravatar || null} name={p.name} size={26} />
                    <span
                      style={{
                        position: 'absolute',
                        right: -1,
                        bottom: -1,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        border: `2px solid ${theme.panel}`,
                        background:
                          p.presence === 'online'
                            ? theme.online
                            : p.presence === 'away'
                              ? theme.away
                              : theme.offline,
                      }}
                    />
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </aside>
  )
}

const sheet: React.CSSProperties = {
  position: 'absolute',
  top: 70,
  right: 18,
  width: 330,
  maxHeight: 'calc(100% - 130px)',
  overflowY: 'auto',
  padding: 14,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  color: theme.text,
  display: 'flex',
  flexDirection: 'column',
}
const sheetCompact: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  maxHeight: '72vh',
  overflowY: 'auto',
  padding: 14,
  paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
  background: theme.panel,
  borderTop: `1px solid ${theme.border}`,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  color: theme.text,
  display: 'flex',
  flexDirection: 'column',
}
const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 20,
  lineHeight: 1,
  padding: '2px 6px',
}
const chip: React.CSSProperties = {
  background: 'none',
  border: `1px solid ${theme.border}`,
  color: theme.textDim,
  borderRadius: 999,
  fontSize: 11,
  padding: '3px 9px',
  cursor: 'pointer',
}
const chipActive: React.CSSProperties = {
  ...chip,
  color: theme.terracottaLite,
  borderColor: '#3d251c',
  background: '#241a15',
}
const messagesBox: React.CSSProperties = {
  marginTop: 8,
  height: 240,
  overflowY: 'auto',
  border: `1px solid ${theme.border}`,
  borderRadius: 10,
  padding: '4px 10px',
  background: '#101218',
}
const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  color: theme.text,
  fontSize: 12.5,
  padding: '8px 10px',
  outline: 'none',
}
const sendBtn: React.CSSProperties = {
  background: theme.terracotta,
  color: '#1a120e',
  border: 'none',
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 700,
  padding: '8px 12px',
  cursor: 'pointer',
}
const sectionTitle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: theme.textDim,
  margin: '0 0 6px',
}
const dimNote: React.CSSProperties = { fontSize: 11.5, color: theme.textDim, padding: '8px 0' }
